import { type IActiveElectionData } from '@interface/IElection';
import { type ILocationLGAInState, type ILocationState, type ILocationWardData } from '@interface/ILocation';
import type React from 'react';
import { useCallback, useEffect } from 'react';

import { ActiveFilters } from './ActiveFilters';
import { AdvancedFilters } from './AdvancedFilters';
import { FilterBar } from './FilterBar';
import { FiltersContainer } from './styled';
import { type ElectionStatus, type ElectionType, useElectionFilters } from './useElectionFilters';

interface ElectionFiltersContainerProps {
	elections: IActiveElectionData[] | undefined;
	states: ILocationState[] | undefined;
	statesLoading: boolean;
	lgas: ILocationLGAInState[] | undefined;
	lgasLoading: boolean;
	wards: ILocationWardData[] | undefined;
	wardsLoading: boolean;
	onFetchStates: () => void;
	onFetchLgas: (stateId: number) => void;
	onFetchWards: (lgaId: number) => void;
	onFilteredElectionsChange: (elections: IActiveElectionData[]) => void;
}

export const ElectionFiltersContainer: React.FC<ElectionFiltersContainerProps> = ({
	elections,
	states,
	statesLoading,
	lgas,
	lgasLoading,
	wards,
	wardsLoading,
	onFetchStates,
	onFetchLgas,
	onFetchWards,
	onFilteredElectionsChange,
}) => {
	const {
		filters,
		filteredElections,
		activeFilters,
		advancedFilterCount,
		isAdvancedOpen,
		setIsAdvancedOpen,
		updateFilter,
		toggleElectionType,
		clearElectionTypes,
		selectAllElectionTypes,
		removeFilter,
		clearAllFilters,
		resetAdvancedFilters,
		resetLgaAndBelow,
		resetWard,
	} = useElectionFilters(elections);

	// Notify parent of filtered elections changes
	useEffect(() => {
		onFilteredElectionsChange(filteredElections);
	}, [filteredElections, onFilteredElectionsChange]);

	// Fetch states when advanced filters open
	useEffect(() => {
		if (isAdvancedOpen && !states?.length) {
			onFetchStates();
		}
	}, [isAdvancedOpen, states, onFetchStates]);

	const handleSearchChange = useCallback(
		(value: string) => {
			updateFilter('searchQuery', value);
		},
		[updateFilter]
	);

	const handleStatusChange = useCallback(
		(status: ElectionStatus | null) => {
			updateFilter('status', status);
		},
		[updateFilter]
	);

	const handleToggleAdvanced = useCallback(() => {
		setIsAdvancedOpen((prev) => !prev);
	}, [setIsAdvancedOpen]);

	const handleStateChange = useCallback(
		(stateId: number | null) => {
			updateFilter('stateId', stateId);
			resetLgaAndBelow();
		},
		[updateFilter, resetLgaAndBelow]
	);

	const handleLgaChange = useCallback(
		(lgaId: number | null) => {
			updateFilter('lgaId', lgaId);
			resetWard();
		},
		[updateFilter, resetWard]
	);

	const handleFetchLgas = useCallback(
		(stateId: number) => {
			onFetchLgas(stateId);
		},
		[onFetchLgas]
	);

	const handleFetchWards = useCallback(
		(lgaId: number) => {
			onFetchWards(lgaId);
		},
		[onFetchWards]
	);

	return (
		<FiltersContainer>
			<FilterBar
				filters={filters}
				advancedFilterCount={advancedFilterCount}
				isAdvancedOpen={isAdvancedOpen}
				onSearchChange={handleSearchChange}
				onToggleElectionType={toggleElectionType}
				onClearElectionTypes={clearElectionTypes}
				onSelectAllElectionTypes={selectAllElectionTypes}
				onStatusChange={handleStatusChange}
				onToggleAdvanced={handleToggleAdvanced}
			/>

			<ActiveFilters
				filters={activeFilters}
				onRemoveFilter={removeFilter}
				onClearAll={clearAllFilters}
			/>

			<AdvancedFilters
				isOpen={isAdvancedOpen}
				filters={filters}
				states={states}
				statesLoading={statesLoading}
				lgas={lgas}
				lgasLoading={lgasLoading}
				wards={wards}
				wardsLoading={wardsLoading}
				onFilterChange={updateFilter}
				onStateChange={handleStateChange}
				onLgaChange={handleLgaChange}
				onReset={resetAdvancedFilters}
				onFetchLgas={handleFetchLgas}
				onFetchWards={handleFetchWards}
			/>
		</FiltersContainer>
	);
};

// Re-export types and utilities
export { type ElectionStatus, type ElectionType, type ParsedElection, parseElectionName } from './useElectionFilters';
